import { useState, useCallback } from 'react';
import { api } from '../api';

const INITIAL_MARKET_DATA = {
  question: '',
  description: '',
  category: 'other',
  outcomes: [],
  isPublic: true,
  resolutionDate: null,
  startDate: null,
  resolutionMethod: 'ai',
  liquidity: 100,
  currency: 'usdr',
};

export const useMarketCreation = () => {
  const [marketData, setMarketData] = useState(INITIAL_MARKET_DATA);
  const [currentStep, setCurrentStep] = useState('question');
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const addMessage = useCallback((message) => {
    const newMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  const updateMarketData = useCallback((updates) => {
    setMarketData(prev => ({ ...prev, ...updates }));
  }, []);

  const processQuestion = useCallback(async (question) => {
    setIsProcessing(true);

    try {
      const result = await api.processQuestion(question);

      const defaultResolution = new Date();
      defaultResolution.setDate(defaultResolution.getDate() + 7);

      updateMarketData({
        question,
        description: result.description,
        category: result.category,
        outcomes: result.outcomes,
        resolutionDate: defaultResolution,
        isPublic: true,
      });

      setIsProcessing(false);
      setCurrentStep('liquidity');

      return {
        description: result.description,
        outcomes: result.outcomes,
        category: result.category,
      };
    } catch {
      setIsProcessing(false);
      throw new Error('Failed to process question');
    }
  }, [updateMarketData]);

  const confirmDescription = useCallback((description) => {
    updateMarketData({ description });
    setCurrentStep('liquidity');
  }, [updateMarketData]);

  const confirmOutcomes = useCallback((outcomes) => {
    updateMarketData({ outcomes });
    setCurrentStep('visibility');
  }, [updateMarketData]);

  const setVisibility = useCallback((isPublic) => {
    updateMarketData({ isPublic });
    setCurrentStep('resolution');
  }, [updateMarketData]);

  const setResolver = useCallback((method) => {
    updateMarketData({ resolutionMethod: method });
    setCurrentStep('resolution');
  }, [updateMarketData]);

  const setResolution = useCallback((date) => {
    updateMarketData({ resolutionDate: date });
    setCurrentStep('liquidity');
  }, [updateMarketData]);

  const setLiquidity = useCallback((amount) => {
    updateMarketData({ liquidity: amount });
  }, [updateMarketData]);

  const confirmMarket = useCallback(async () => {
    setIsProcessing(true);
    setCurrentStep('deploying');
  }, []);

  const finishDeploy = useCallback(() => {
    setIsProcessing(false);
    setCurrentStep('success');
  }, []);

  const reset = useCallback(() => {
    setMarketData(INITIAL_MARKET_DATA);
    setCurrentStep('question');
    setMessages([]);
    setIsProcessing(false);
  }, []);

  return {
    marketData,
    currentStep,
    messages,
    isProcessing,
    addMessage,
    updateMarketData,
    processQuestion,
    confirmDescription,
    confirmOutcomes,
    setVisibility,
    setResolution,
    setResolver,
    setLiquidity,
    confirmMarket,
    finishDeploy,
    reset,
    setCurrentStep,
  };
};
